import {
  hslToColorString,
  parseToHsl,
  setLightness,
  transparentize,
} from 'polished';

import {
  AVAILABLE_FONTS,
  LIGHTNESS_THRESHOLD,
  defaultFontName,
  getDefaultMKColors,
} from './constants';
import { Font } from './useAgencyBranding';

type Theme = 'light' | 'dark';

/**
 * Normalizes lightness values to stay within 0-1 range.
 * If a value exceeds 1, it wraps around (e.g., 1.2 becomes 0.8)
 * If a value is below 0, it wraps around (e.g., -0.2 becomes 0.8)
 *
 * @param lightness The lightness value to normalize
 * @returns A normalized lightness value between 0 and 1
 */
export function normalizeLightness(lightness: number): number {
  // First handle values below 0
  if (lightness < 0) {
    // Convert negative values by adding 1 until we're in range
    // For example: -0.3 becomes 0.7
    return normalizeLightness(1 + lightness);
  }

  // Then handle values above 1
  if (lightness > 1) {
    // We need to calculate the "overflow" and subtract from 1
    // For example: 1.2 has an overflow of 0.2, so result is 0.8
    const overflow = lightness % 1;
    return 1 - overflow;
  }

  // Value is already in range
  return lightness;
}

export const isValidColor = (strColor?: string) => {
  if (!strColor || !global.window) return false;
  const s = new window.Option().style;
  s.color = strColor;
  return s.color !== '';
};

export const getTheme = (backgroundColor: string): Theme => {
  if (!isValidColor(backgroundColor)) return 'light';
  return parseToHsl(backgroundColor).lightness > LIGHTNESS_THRESHOLD
    ? 'light'
    : 'dark';
};

const getSecondaryColor = (primaryColor: string, secondaryColor?: string) => {
  if (isValidColor(secondaryColor)) return secondaryColor;
  const lightness = parseToHsl(primaryColor).lightness;
  const newLightness = lightness <= 0.5 ? lightness + 0.2 : lightness - 0.2;
  return setLightness(normalizeLightness(newLightness), primaryColor);
};
const getTertiaryColor = (primaryColor: string) => {
  const lightness = parseToHsl(primaryColor).lightness;
  const newLightness = lightness <= 0.5 ? lightness + 0.35 : lightness - 0.35;
  return setLightness(normalizeLightness(newLightness), primaryColor);
};
const getBtnHoverColor = (primaryColor: string) => {
  const lightness = parseToHsl(primaryColor).lightness;
  const newLightness = lightness <= 0.1 ? lightness + 0.05 : lightness - 0.05;
  return setLightness(normalizeLightness(newLightness), primaryColor);
};
const getWidgetHoverColor = (primaryColor: string) => {
  const lightness = parseToHsl(primaryColor).lightness;
  const newLightness = lightness <= 0.85 ? 0.9 : 0.8;
  return setLightness(normalizeLightness(newLightness), primaryColor);
};
const getWidgetDragColor = (primaryColor: string) => {
  const lightness = parseToHsl(primaryColor).lightness;
  const newLightness = lightness <= 0.85 ? 0.85 : 0.75;
  return setLightness(normalizeLightness(newLightness), primaryColor);
};

export const getMediaKitColors = ({
  primaryColor = '',
  secondaryColor = '',
  backgroundColor = '#ffffff',
}: {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}) => {
  if (!isValidColor(primaryColor)) return getDefaultMKColors();
  const hslColor = parseToHsl(primaryColor);
  const primary = hslToColorString(hslColor);

  const bgPrimary = backgroundColor;
  const theme = getTheme(bgPrimary);

  const secondary = getSecondaryColor(primary, secondaryColor);

  const tertiary = getTertiaryColor(primary);

  const bgSecondary =
    theme === 'light'
      ? setLightness(0.98, primary)
      : setLightness(0.02, primary);

  const bgAlpha = transparentize(0.65, primary);

  const btnHover = getBtnHoverColor(primary);

  const widgetBgHover = getWidgetHoverColor(primary);
  const widgetBgDrag = getWidgetDragColor(primary);
  const bgSkeleton = widgetBgDrag;

  const mkColors = {
    '--media-kit-primary': primary,
    '--media-kit-secondary': secondary,
    '--media-kit-tertiary': tertiary,

    '--media-kit-background-primary': bgPrimary,
    '--media-kit-background-secondary': bgSecondary,
    '--media-kit-background-alpha': bgAlpha,
    '--media-kit-background-skeleton': bgSkeleton,

    '--media-kit-text-base':
      theme === 'light'
        ? 'var(--wb-root-text-color-base)'
        : 'var(--wb-root-inverse-text-color-base)',
    '--media-kit-text-subtle':
      theme === 'light'
        ? 'var(--wb-root-text-color-subtle)'
        : 'var(--wb-root-inverse-text-color-subtle)',

    '--media-kit-button-background-default': primary,
    '--media-kit-button-background-hover': btnHover,
    '--media-kit-button-text': bgPrimary,

    '--media-kit-chart-background-primary': primary,
    '--media-kit-chart-background-secondary': secondary,
    '--media-kit-chart-background-tertiary': tertiary,

    '--media-kit-widget-background-hover': widgetBgHover,
    '--media-kit-widget-background-drag': widgetBgDrag,
  };

  return mkColors;
};

// Function to load a Google Font
export const applyMediaKitFont = (font?: Font | null) => {
  if (!font)
    font = AVAILABLE_FONTS.find((f) => f.name === defaultFontName) as Font;

  // Create a unique ID for the font link element
  const linkId = `font-link-${font.name.replace(/\s+/g, '-').toLowerCase()}`;

  // Check if the font is already loaded
  if (!document.getElementById(linkId)) {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${font.name.replace(
      /\s+/g,
      '+',
    )}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }

  document.documentElement.style.setProperty(
    '--media-kit-font-family',
    font.family,
  );
};
